import { useEffect, useState } from 'react';

import { CheckCircle, ClipboardList, Download, Shield, Trophy } from 'lucide-react';

const TEAMS = [
  { rank: 1, emoji: '🥇', name: 'Dubois / Bernard' },
  { rank: 2, emoji: '🥈', name: 'Garcia / Lopez' },
  { rank: 3, emoji: '🥉', name: 'Petit / Morel' },
];

const PODIUM = [
  { rank: 2, emoji: '🥈', name: 'Garcia / Lopez', h: 'h-16' },
  { rank: 1, emoji: '🥇', name: 'Dubois / Bernard', h: 'h-24' },
  { rank: 3, emoji: '🥉', name: 'Petit / Morel', h: 'h-12' },
];

export default function ViewResultats() {
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 800);
    const t2 = setTimeout(() => setStep(2), 1800);
    const t3 = setTimeout(() => setStep(3), 2800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  return (
    <div className='grid md:grid-cols-2 gap-10 items-start demo-view-enter'>
      {/* Left — saisie classement */}
      <div className='space-y-5'>
        <div className='inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 px-3 py-1.5 rounded-full'>
          <ClipboardList className='w-3.5 h-3.5 text-violet-500 dark:text-violet-400' />
          <span className='text-xs font-bold text-violet-600 dark:text-violet-400'>Saisie du classement final</span>
        </div>

        <div>
          <h3 className='text-2xl font-black text-slate-900 dark:text-white mb-2'>Publiez le podium en un clic</h3>
          <p className='text-slate-500 dark:text-slate-400 text-sm leading-relaxed'>
            Saisissez les rangs (ex-æquos permis), puis terminez le concours. Le podium public est publié
            instantanément.
          </p>
        </div>

        <div className='bg-slate-100 border border-slate-200 dark:bg-slate-800/80 dark:border-slate-700 rounded-2xl p-4 space-y-3'>
          <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest'>Classement final</p>
          {TEAMS.map((team, i) => (
            <div
              key={team.rank}
              className={`flex items-center gap-3 bg-white border rounded-xl px-4 py-3 transition-all duration-500 dark:bg-slate-900/60 ${
                step > i
                  ? 'border-emerald-200 dark:border-emerald-500/30 opacity-100'
                  : 'border-slate-200 dark:border-slate-800 opacity-30'
              }`}
            >
              <span className='text-xl leading-none'>{team.emoji}</span>
              <div className='flex-1 min-w-0'>
                <p className='font-bold text-slate-900 dark:text-white text-sm truncate'>{team.name}</p>
              </div>
              <span className='text-xs font-black text-slate-500 dark:text-slate-400'>#{team.rank}</span>
              {step > i && <CheckCircle className='w-4 h-4 text-emerald-500 dark:text-emerald-400 shrink-0' />}
            </div>
          ))}
        </div>

        <div className='flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 dark:bg-slate-900/50 dark:border-slate-800 rounded-xl px-4 py-3'>
          <Download className='w-3.5 h-3.5 shrink-0 mt-0.5' />
          <span>Export CSV du classement disponible après clôture.</span>
        </div>
      </div>

      {/* Right — podium public + bouton terminer */}
      <div className='space-y-4'>
        <div className='bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 rounded-2xl p-5'>
          <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4'>Podium public</p>

          <div className='flex items-end justify-center gap-4 mb-5'>
            {PODIUM.map(p => (
              <div key={p.rank} className='flex flex-col items-center gap-1 flex-1'>
                <span className='text-2xl'>{p.emoji}</span>
                <p className='text-[10px] font-bold text-slate-700 dark:text-slate-300 text-center leading-tight'>
                  {p.name}
                </p>
                <div
                  className={`${p.h} w-full rounded-t-xl ${
                    p.rank === 1
                      ? 'bg-amber-400/80 dark:bg-amber-500/60'
                      : p.rank === 2
                        ? 'bg-slate-300 dark:bg-slate-600'
                        : 'bg-amber-700/60 dark:bg-amber-800/50'
                  }`}
                />
              </div>
            ))}
          </div>

          {step === 3 && (
            <div className='flex justify-center'>
              <span className='bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider'>
                Podium publié
              </span>
            </div>
          )}
        </div>

        <button
          className={`w-full py-4 rounded-2xl font-black text-base flex items-center justify-center gap-3 transition-all duration-500 ${
            step === 3
              ? 'bg-linear-to-r from-violet-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-violet-600/30 dark:hover:shadow-violet-900/40 hover:-translate-y-0.5'
              : 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-600'
          }`}
        >
          <Trophy className='w-5 h-5' />
          {step === 3 ? 'Terminer le concours' : 'Saisie en cours…'}
        </button>

        <div className='flex items-center justify-center gap-5 text-xs text-slate-500 dark:text-slate-600'>
          <span className='flex items-center gap-1.5'>
            <Shield className='w-3 h-3' />
            Podium et résultats
          </span>
          <span className='flex items-center gap-1.5'>
            <CheckCircle className='w-3 h-3' />
            Ex-æquos supportés
          </span>
        </div>
      </div>
    </div>
  );
}
